import React, { useState, useEffect, Fragment } from 'react';
import { connect } from 'react-redux';
import { withTranslation } from 'react-i18next';
import { Field, reduxForm, formValueSelector } from 'redux-form';
import { ButtonToolbar, Button } from 'reactstrap';
import PropTypes from 'prop-types';
import ReactRouterPropTypes from 'react-router-prop-types';
import MaterialTable from 'material-table';
import IconButton from '@material-ui/core/IconButton';
import AddIcon from '@material-ui/icons/Add';
import Tooltip from '@material-ui/core/Tooltip';
import { cpfMasker } from '../../../shared/components/form/Masks';
import { validateEmail } from '../../../shared/validate/validate';
import { responsibleFindByEmailQuery } from '../../../shared/queries/Responsible';
import { showNotificationAutoDestroy } from '../../../shared/components/ShowNotification';
import renderField from '../../../shared/components/form/RenderField';
import Modal from '../../../shared/components/Modal';
import Select from '../../../shared/components/form/Select';
import GraphQL from '../../../GraphQL';
import validate from './Validate';
import columns from './ColumnsRelations';
import genderSelector from '../../../shared/components/form/GenderSelector';
import AddressInfo from '../../../shared/components/form/AddressInfo';
import renderCheckBoxField from '../../../shared/components/form/CheckBox';
import {
  responsibleRelatedStudentListByRespIdQuery,
  responsibleUnRelatedStudentListByRespIdQuery,
  responsibleStudentRelationRemoveMutation,
} from '../../../shared/queries/ResponsibleStudentRelation';
import responsibleKindShipList from '../../../shared/validate/responsibleKindShipList';
import materialTableIcons from '../../../shared/utils/materialTableIcons';
import SelectVirtualized from '../../../shared/components/form/SelectVirtualized';
import SmartAvatar from '../../../shared/components/form/SmartAvatar';
import DateField from '../../../shared/components/form/DateField';

let ResponsibleForm = (props) => {
  const {
    handleSubmit,
    t,
    showLoading,
    hideLoading,
    initialize,
    fullName,
    change,
    backHandler,
    selectedEmail,
    studentForm,
    studentRelationData,
    cancelEdit,
    selectedEmailChangeHandler,
  } = props;
  const [canSave, setCanSave] = useState(false);
  const [dto, setDto] = useState();
  const [unRelatedStudents, setUnRelatedStudents] = useState([]);
  const [relatedStudents, setRelatedStudents] = useState([]);
  const [relationToRemove, setRelationToRemove] = useState({});
  const [modal, setModal] = useState(false);
  const [relationToAdd, setRelationToAdd] = useState({});
  const [fileUrl, setFileUrl] = useState();

  useEffect(() => {
    initialize(dto);
    if (dto && dto.fileId) {
      setFileUrl(dto.profilePhoto);
      change('fileUrl', dto.profilePhoto);
      initialize(dto);
    }
  }, [dto]);

  useEffect(() => {
    change('fullName', fullName);
  }, [fullName]);

  const loadRelatedStudents = (responsibleId) => {
    if (responsibleId) {
      showLoading();
      const query = responsibleRelatedStudentListByRespIdQuery(responsibleId);
      GraphQL(query, {})
        .then((res) => {
          if (res.responsibleRelatedStudentListByRespId) {
            setRelatedStudents(res.responsibleRelatedStudentListByRespId);
            change(
              'relatedStudents',
              res.responsibleRelatedStudentListByRespId
            );
          }
          hideLoading();
        })
        .catch(() => {
          hideLoading();
        });
    } else {
      setRelatedStudents([]);
    }
  };

  const loadUnRelatedStudents = (responsibleId) => {
    showLoading();
    const query = responsibleUnRelatedStudentListByRespIdQuery(responsibleId);
    GraphQL(query, {})
      .then((res) => {
        if (res.responsibleUnRelatedStudentListByRespId) {
          const loadedUnRelatedStudents =
            res.responsibleUnRelatedStudentListByRespId.map((item) => ({
              ...item,
              value: item.student.id,
              label: item.student.fullName,
            }));
          setUnRelatedStudents(loadedUnRelatedStudents);
        }
        hideLoading();
      })
      .catch(() => {
        hideLoading();
      });
  };

  const loadResponsibleByEmail = (email) => {
    showLoading();
    const query = responsibleFindByEmailQuery(email);
    GraphQL(query, {})
      .then((res) => {
        hideLoading();
        if (res.responsibleFindByEmail) {
          res.responsibleFindByEmail = {
            ...res.responsibleFindByEmail,
            city: {
              ...res.responsibleFindByEmail.city,
              value: res.responsibleFindByEmail.city.id,
              label: res.responsibleFindByEmail.city.name,
            },
            state: {
              ...res.responsibleFindByEmail.state,
              value: res.responsibleFindByEmail.state.id,
              label: res.responsibleFindByEmail.state.name,
            },
            birthDate: res.responsibleFindByEmail.birthDate
              ? new Date(`${res.responsibleFindByEmail.birthDate}T00:00:00`)
              : 0,
            accessStudentData: studentRelationData.accessStudentData || true,
            emergencyContact: studentRelationData.emergencyContact,
            financialContact: studentRelationData.financialContact,
            takeStudent: studentRelationData.takeStudent,
            relationKindShip: studentRelationData.type
              ? {
                  value: studentRelationData.type,
                  label: t(`responsible-kindship.${studentRelationData.type}`),
                }
              : { value: 'U', label: t(`responsible-kindship.${'U'}`) },
          };
        } else {
          res.responsibleFindByEmail = {
            id: 0,
            email,
            gender: 'O',
            accessStudentData: true,
          };
        }
        setDto(res.responsibleFindByEmail);
        setCanSave(true);
        if (!studentForm) {
          loadRelatedStudents(res.responsibleFindByEmail.id);
          loadUnRelatedStudents(res.responsibleFindByEmail.id);
        }
      })
      .catch(() => {
        hideLoading();
        showNotificationAutoDestroy(
          'danger',
          t('notifications.error'),
          t('notifications.find-record-error')
        );
      });
  };

  const handleEmailBlur = (evt) => {
    const email = evt.target.value;
    if (email) {
      if (!validateEmail(email)) {
        showNotificationAutoDestroy(
          'warning',
          t('warnings.title'),
          t('warnings.invalid-mail')
        );
      } else {
        selectedEmailChangeHandler(email);
        loadResponsibleByEmail(email);
      }
    }
  };

  const toggleModal = () => {
    setModal(!modal);
  };

  const removeItemClickHandler = (evt, rowData) => {
    setRelationToRemove({ ...rowData });
    toggleModal();
  };

  const updateRelationHandler = (data, newData) => {
    const updatedRelatedStudents = relatedStudents.slice();
    const itemIndex = updatedRelatedStudents.indexOf(data);
    const dataToUpdate = {
      ...newData,
    };
    updatedRelatedStudents[itemIndex] = dataToUpdate;
    setRelatedStudents(updatedRelatedStudents);
    change('relatedStudents', updatedRelatedStudents);
  };

  const onUnRelatedStudentChange = (value) => {
    const updatedValue = {
      ...value,
      accessStudentData: true,
    };
    setRelationToAdd(updatedValue);
  };

  const onAddRelationClick = () => {
    if (relationToAdd && relationToAdd.student) {
      const updatedRelationStudents = relatedStudents.slice();
      updatedRelationStudents.push(relationToAdd);
      const updatedUnRelationStudents = unRelatedStudents.filter(
        (item) => item.student.id !== relationToAdd.student.id
      );
      setRelatedStudents(updatedRelationStudents);
      setUnRelatedStudents(updatedUnRelationStudents);
      setRelationToAdd(null);
      change('relatedStudents', updatedRelationStudents);
      change('studentRelation', null);
    }
  };

  const deleteItem = () => {
    toggleModal();
    if (relationToRemove.id) {
      showLoading();
      const removeQuery = responsibleStudentRelationRemoveMutation(
        relationToRemove.id
      );
      GraphQL(removeQuery, {})
        .then(() => {
          showNotificationAutoDestroy(
            'success',
            t('success.title'),
            t('success.successfully-removed')
          );
          hideLoading();
        })
        .catch(() => {
          hideLoading();
          showNotificationAutoDestroy(
            'danger',
            t('errors.title'),
            t('errors.error-to-remove')
          );
        });

      loadRelatedStudents(dto.id);
    } else {
      const updatedRelatedStudents = relatedStudents.filter(
        (item) => item.student.id !== relationToRemove.student.id
      );
      setRelatedStudents(updatedRelatedStudents);
      change('relatedStudents', updatedRelatedStudents);
    }
    loadUnRelatedStudents(dto.id);
  };

  const ResponsiblePhotoChangeHandler = (file) => {
    setFileUrl(file.fileUrl);
    change('fileId', file.fileId);
    change('profilePhoto', file.fileUrl);
  };

  useEffect(() => {
    if (selectedEmail && selectedEmail !== '0') {
      loadResponsibleByEmail(selectedEmail);
    } else {
      setDto({});
    }
  }, [selectedEmail]);

  return (
    <Fragment>
      <form className="form" onSubmit={handleSubmit}>
        <div className="form__form-group">
          <div className="form__form-group--centered">
            <SmartAvatar
              name="fileId"
              avatarSrc={fileUrl}
              avatarChangeHandler={ResponsiblePhotoChangeHandler}
            />
          </div>
        </div>
        <div className="form__form-group--3_columns">
          <div className="form__form-group">
            <span className="form__form-group-label">{t('general.email')}</span>
            <div className="form__form-group-field">
              <Field
                name="email"
                type="email"
                component={renderField}
                onBlur={handleEmailBlur}
                maxLength="200"
                placeholder={t('placeholder.responsible.email')}
              />
            </div>
          </div>
          <div className="form__form-group">
            <span className="form__form-group-label">{t('general.cpf')}</span>
            <div className="form__form-group-field">
              <Field
                name="cpf"
                type="text"
                component={renderField}
                {...cpfMasker()}
                disabled={!canSave}
              />
            </div>
          </div>
          <div className="form__form-group">
            <span className="form__form-group-label">{t('general.rg')}</span>
            <div className="form__form-group-field">
              <Field
                name="rg"
                type="text"
                component={renderField}
                disabled={!canSave}
                maxLength="25"
                placeholder={t('placeholder.responsible.rg')}
              />
            </div>
          </div>
        </div>
        <div className="form__form-group--3_columns">
          <div className="form__form-group">
            <span className="form__form-group-label">
              {t('general.first-name')}
            </span>
            <div className="form__form-group-field">
              <Field
                name="firstName"
                type="text"
                component={renderField}
                maxLength="100"
                placeholder={t('placeholder.responsible.first-name')}
                disabled={!canSave}
              />
            </div>
          </div>
          <div className="form__form-group">
            <span className="form__form-group-label">
              {t('general.last-name')}
            </span>
            <div className="form__form-group-field">
              <Field
                name="lastName"
                type="text"
                component={renderField}
                maxLength="100"
                placeholder={t('placeholder.responsible.last-name')}
                disabled={!canSave}
              />
            </div>
          </div>
          <div className="form__form-group">
            <span className="form__form-group-label">
              {t('general.date-of-birth')}
            </span>
            <div className="form_form-group-field">
              <Field
                name="birthDate"
                component={DateField}
                placeholder={t('placeholder.student.dateOfBirth')}
                maxDate={new Date()}
                disabled={!canSave}
              />
            </div>
          </div>
        </div>
        <div className="form__form-group--2_columns">
          <div className="form__form-group">
            <span className="form__form-group-label">
              {t('general.full-name')}
            </span>
            <div className="form__form-group-field">
              <Field
                name="fullName"
                type="text"
                component={renderField}
                maxLength="100"
                placeholder={t('placeholder.responsible.full-name')}
                disabled
              />
            </div>
          </div>
          <div className="form__form-group">
            <span className="form__form-group-label">
              {t('general.gender')}
            </span>
            <div className="form__form-group-field">
              <Field
                name="gender"
                component={genderSelector}
                t={t}
                isDisabled={!canSave}
              />
            </div>
          </div>
        </div>
        <AddressInfo
          t={t}
          showLoading={showLoading}
          hideLoading={hideLoading}
          change={change}
          disabled={!canSave}
        />
        {studentForm ? (
          <Fragment>
            <div className="form__form-group">
              <span className="form__form-group-label">
                {t('general.bond')}
              </span>
              <div className="form__form-group-field">
                <Field
                  name="relationKindShip"
                  type="text"
                  options={responsibleKindShipList(t)}
                  component={Select}
                  placeholder={t('placeholder.responsible.select-bond')}
                  isDisabled={!canSave}
                />
              </div>
            </div>
            <div className="form__form-group--4_columns">
              <div className="form__form-group">
                <span className="form__form-group-label">
                  {t('general.access-student-data')}
                </span>
                <Field
                  name="accessStudentData"
                  component={renderCheckBoxField}
                  className="colored-click"
                />
              </div>
              <div className="form__form-group">
                <span className="form__form-group-label">
                  {t('general.take-student')}
                </span>
                <Field
                  name="takeStudent"
                  component={renderCheckBoxField}
                  className="colored-click"
                />
              </div>
              <div className="form__form-group">
                <span className="form__form-group-label">
                  {t('general.financial-contact')}
                </span>
                <Field
                  name="financialContact"
                  component={renderCheckBoxField}
                  className="colored-click"
                />
              </div>
              <div className="form__form-group">
                <span className="form__form-group-label">
                  {t('general.emergency-contact')}
                </span>
                <Field
                  name="emergencyContact"
                  component={renderCheckBoxField}
                  className="colored-click"
                />
              </div>
              <ButtonToolbar className="form__button-toolbar">
                <div className="form__form-group-field flex-1">
                  <Button
                    color="danger"
                    disabled={!canSave}
                    onClick={cancelEdit}
                  >
                    {t('general.clean')}
                  </Button>
                  <Button
                    color="success"
                    type="button"
                    onClick={handleSubmit}
                    disabled={!canSave}
                  >
                    {Object.keys(studentRelationData).length === 0
                      ? t('general.add')
                      : t('general.update')}
                  </Button>
                </div>
              </ButtonToolbar>
            </div>
          </Fragment>
        ) : (
          <Fragment>
            <div className="form__form-group">
              <span className="form__form-group-label">
                {t('general.student')}
              </span>
              <div className="form__form-group-field">
                <Field
                  name="studentRelation"
                  type="text"
                  options={unRelatedStudents}
                  component={SelectVirtualized}
                  placeholder={t('placeholder.responsible.select-students')}
                  isDisabled={!canSave}
                  onChange={onUnRelatedStudentChange}
                />
                <span className="pl-3">
                  <Tooltip
                    title={canSave ? t('general.add-student') : ''}
                    arrow
                  >
                    <IconButton
                      aria-label="add"
                      onClick={onAddRelationClick}
                      disabled={!canSave}
                    >
                      <AddIcon />
                    </IconButton>
                  </Tooltip>
                </span>
              </div>
            </div>
            <div className="form__form-group">
              <MaterialTable
                data={relatedStudents}
                icons={materialTableIcons}
                columns={columns(t, updateRelationHandler)}
                actions={[
                  {
                    icon: 'delete',
                    tooltip: t('general.tables.delete_registry'),
                    onClick: (evt, rowData) =>
                      removeItemClickHandler(evt, rowData),
                  },
                ]}
                options={{
                  paging: false,
                  showTitle: false,
                  search: false,
                  toolbar: false,
                  actionsColumnIndex: 8,
                }}
                localization={{
                  pagination: {
                    labelRowsSelect: t('general.tables.rows'),
                    labelDisplayedRows: `{from}-{to} ${t(
                      'general.tables.labelDisplayRows'
                    )} {count}`,
                    firstTooltip: t('general.tables.firstTooltip'),
                    previousTooltip: t('general.tables.firstTooltip'),
                    nextTooltip: t('general.tables.firstTooltip'),
                    lastTooltip: t('general.tables.firstTooltip'),
                  },

                  header: {
                    actions: t('general.tables.actions'),
                  },
                  toolbar: {
                    exportTitle: t('general.tables.export_title'),
                    exportAriaLabel: t('general.tables.export'),
                    exportCSVName: t('general.tables.exportCSVName'),
                    exportPDFName: t('general.tables.exportPDFName'),
                    searchPlaceholder: t('general.tables.search_placeholder'),
                    nRowsSelected: `{0} ${t('general.tables.nRowsSelected')}`,
                  },
                  body: {
                    emptyDataSourceMessage: t(
                      'general.tables.no-data-to-display'
                    ),
                    exportTitle: t('general.tables.export_title'),
                    exportAriaLabel: t('general.export'),
                    exportCSVName: t('general.tables.exportCSVName'),
                    exportPDFName: t('general.exportPDFName'),
                    searchPlaceholder: t('general.tables.search_placeholder'),
                  },
                }}
              />
            </div>
          </Fragment>
        )}
        {studentForm ? null : (
          <ButtonToolbar className="form__button-toolbar">
            <Button color="primary" onClick={backHandler}>
              {t('general.back')}
            </Button>
            <Button color="success" type="submit" disabled={!canSave}>
              {t('general.save')}
            </Button>
          </ButtonToolbar>
        )}
      </form>
      <Modal
        color="warning"
        title={t('notifications.attention')}
        btn="Warning"
        message={t('notifications.delete_modal')}
        toggle={toggleModal}
        modal={modal}
        onConfirm={deleteItem}
      />
    </Fragment>
  );
};

ResponsibleForm.propTypes = {
  handleSubmit: PropTypes.func.isRequired,
  t: PropTypes.func.isRequired,
  showLoading: PropTypes.func.isRequired,
  hideLoading: PropTypes.func.isRequired,
  initialize: PropTypes.func.isRequired,
  change: PropTypes.func.isRequired,
  backHandler: PropTypes.func.isRequired,
  fullName: PropTypes.string,
  selectedEmail: PropTypes.string.isRequired,
  studentForm: PropTypes.bool,
  cancelEdit: PropTypes.func,
  history: ReactRouterPropTypes.history.isRequired,
  studentRelationData: PropTypes.objectOf(PropTypes.shape),
  selectedEmailChangeHandler: PropTypes.func,
};

ResponsibleForm.defaultProps = {
  fullName: '',
  studentForm: false,
  studentRelationData: {},
  cancelEdit: null,
  selectedEmailChangeHandler: () => {},
};

ResponsibleForm = reduxForm({
  form: 'ResponsibleForm',
  validate,
  keepDirtyOnReinitialize: true,
  enableReinitialize: true,
})(ResponsibleForm);

const selector = formValueSelector('ResponsibleForm');

ResponsibleForm = connect((state) => {
  const { firstName, lastName } = selector(state, 'firstName', 'lastName');
  const newState = {
    firstName,
    lastName,
    fullName: `${firstName || ''} ${lastName || ''}`,
  };
  return newState;
})(ResponsibleForm);

export default withTranslation('common')(ResponsibleForm);
